import { BaseApiClient } from "@eusoft/webapp-framework";
import { nativeApp } from "./NativePayment";
import { CommandQueries, type CommandMap, type Guid } from "../entities/Commands";
import type { INtNotification, NtSource, PaymentResult } from "../entities/Types";

class PmApiClient extends BaseApiClient {

    executeAsync<TName extends keyof CommandMap, TResult extends CommandMap[TName]["result"]>(name: TName, data?: Partial<CommandMap[TName]["command"]>, method?: "GET"|"POST"): Promise<TResult> {

        const uri = "/command/" + encodeURIComponent("PaymentManager.Entities." + name);

        console.info("EXECUTING " + name);

        if (!method) {
            if (CommandQueries[name] === false)
                method = "POST";
            else
                method = "GET";
        }

        if (method == "GET")
            return this.requestJsonAsync<TResult>(uri, "GET", null, {
                body: JSON.stringify(data ?? {})
            });

        return this.requestJsonAsync<TResult>(uri, "POST", data ?? {});
    }

    getCommandUrl<TName extends keyof CommandMap>(name: TName, data?: Partial<CommandMap[TName]["command"]>) {

        const path = "/command/" + encodeURIComponent("PaymentManager.Entities." + name);

        const url = this.buildUrl(path, {
            body: JSON.stringify(data ?? {})
        });

        return url;
    }

    async executeSignAsync<TName extends keyof CommandMap, TCommand extends CommandMap[TName]["command"], TResult extends CommandMap[TName]["result"]>(name: TName, data: Partial<TCommand>): Promise<TResult> {

        const signedData = await nativeApp.signPayloadAuthAsync(JSON.stringify(data));

        return await this.requestJsonAsync<TResult>(
            "/command/" + encodeURIComponent("PaymentManager.Entities." + name),
            "POST",
            data,
            null, {
                "X-Signature": signedData,
            });
    }

    qrCodeUri(text: string, size: number = 200) {

        return this.buildUrl(`/media/qr/${size}/${encodeURIComponent(text)}`);
    }

    async qrCodeAsync(text: string, size: number = 200) {

        const resp = await this.requestAsync(
            `/media/qr/${size}/${encodeURIComponent(text)}`,
            "GET");

        return resp.blob(); 
    }


    getMessagesAsync(readerId: string, source: NtSource, timeout: number) {

        return this.requestJsonAsync<INtNotification[]>("/messages", "GET", null, {
            readerId,
            source,
            timeout
        });
    }

    createPaymentTokenAsync(readerId: string, amount: number) {
        return this.requestTextAsync("/payment-token", "POST", null, {
            readerId,
            amount
        });
    }

    initCardAsync(readerId: string) {
        return this.requestTextAsync("/init-card", "POST", null, {
            readerId
        });
    }


    setPaymentResultAsync(token: string, result: PaymentResult, msg?: string) {
        return this.requestJsonAsync("/payment-result", "POST", null, {
            token,
            result,
            msg
        });
    }

    endPairAsync(code: string) {
        return this.requestTextAsync("/end-pair", "POST", null, {
            code
        });
    }

    override onPrepareHeaders(headers: Record<string, string>) {
        super.onPrepareHeaders(headers);
        headers["X-Device"] = this.deviceId;
    }

    override get authorization() {
        return this.accessToken ? "Bearer " + this.accessToken : undefined;
    }

    accessToken: string;

    deviceId: Guid;   
}

export const apiClient = new PmApiClient();